<script lang="ts">
  import { getContext } from "svelte";
  import { derived, type Readable } from "svelte/store";
  import { type CreateQueryResult } from "@tanstack/svelte-query";
  import { mdiOpenInNew } from "@mdi/js";

  import { location } from "../../simple-svelte-router";
  import Icon from "../generic/Icon.svelte";

  const config = getContext("useConfig") as CreateQueryResult<Config>;
  const user = getContext("useUser") as CreateQueryResult<User>;
  const vce = getContext("useVce") as CreateQueryResult<CurrentVCE>;
  const messages = getContext("useMessages") as CreateQueryResult<Message[]>;
  const seenMessages = getContext("useSeenMessages") as CreateQueryResult<
    number[]
  >;
  const userRoles = getContext("useUserRoles") as Readable<Role[]>;
  const isAdmin = getContext("useIsAdmin") as Readable<Boolean>;
  const isGroupAdmin = getContext("useIsGroupAdmin") as Readable<Boolean>;
  const ouMenuOpen = getContext("useOuMenuOpen") as Readable<Boolean>;

  const unseenMessages = derived(
    [messages, seenMessages],
    ([messages, seenMessages]) => {
      if (messages.isSuccess && seenMessages.isSuccess) {
        return messages.data.filter((message) => {
          if (seenMessages.data.indexOf(message.id) < 0) {
            return true;
          }
          return false;
        });
      }
      return [];
    },
  );

  const hasClustersAccess = derived(userRoles, (userRoles) => {
    for (const role of userRoles) {
      if (role.name === "admin" || role.name === "cluster-admin") {
        return true;
      }
    }
    return false;
  });

  function hideMainMenu() {
    const toggle = document.querySelector("#ou-mobile-menu-toggle");
    if (toggle) {
      (toggle as HTMLAnchorElement).dispatchEvent(new Event("click"));
    }
  }
</script>

<div
  class="{$ouMenuOpen
    ? 'block'
    : 'hidden'} bg-secondary-mid-blue md:bg-primary-blue-50 text-white md:block md:text-black"
>
  <nav
    aria-label="Open Computing Lab"
    class="flex flex-col lg:container md:flex-row lg:mx-auto lg:space-x-4 lg:px-12"
  >
    <a
      href="#/"
      on:click={hideMainMenu}
      class="ocl-main-nav-entry {$location.pathname === '/'
        ? 'ocl-main-nav-entry-current'
        : ''}"
    >
      {#if $config.isSuccess}
        {$config.data.title}
      {:else}
        Loading configuration. Please wait...
      {/if}
    </a>
    <div role="" class="flex-1" />
    {#if $isAdmin || $hasClustersAccess}
      <a
        href="#/admin"
        on:click={hideMainMenu}
        class="ocl-main-nav-entry {$location.pathname.startsWith('/admin')
          ? 'ocl-main-nav-entry-current'
          : ''}">Administration</a
      >
    {/if}
    {#if $isAdmin || $isGroupAdmin}
      <a
        href="#/dashboard/historical"
        on:click={hideMainMenu}
        class="ocl-main-nav-entry {$location.pathname.startsWith('/dashboard')
          ? 'ocl-main-nav-entry-current'
          : ''}">Dashboard</a
      >
    {/if}
    {#if $user.isSuccess}
      <a
        href="#/messages"
        on:click={hideMainMenu}
        class="ocl-main-nav-entry {$location.pathname.startsWith('/messages')
          ? 'ocl-main-nav-entry-current'
          : ''}"
      >
        <span>Notifications</span>
        {#if $unseenMessages.length > 0}
          <span
            class="bg-secondary-green ml-1 inline-block rounded-full px-2 text-sm text-black"
            >{$unseenMessages.length}</span
          >
        {/if}
      </a>
    {/if}
    {#if $user.isSuccess}
      <a
        href="#/user"
        on:click={hideMainMenu}
        class="ocl-main-nav-entry {$location.pathname.startsWith('/user')
          ? 'ocl-main-nav-entry-current'
          : ''}"
        >{$user.data.name}
      </a>
    {/if}
    <a
      href="https://docs.ocl.open.ac.uk/container-launcher/user"
      on:click={hideMainMenu}
      target="_blank"
      class="ocl-main-nav-entry flex flex-row items-center space-x-1"
    >
      <span>Help</span>
      <Icon path={mdiOpenInNew} class="h-4 w-4" />
    </a>
    {#if $user.isSuccess}
      <a
        href="#/logout"
        on:click={hideMainMenu}
        class="ocl-main-nav-entry {$location.pathname === '/logout'
          ? 'ocl-main-nav-entry-current'
          : ''}">Logout</a
      >
    {/if}
  </nav>
</div>
{#if ($isAdmin || $hasClustersAccess) && $location.pathname.startsWith("/admin")}
  <div class="{$ouMenuOpen ? 'block' : 'hidden'} md:block">
    <nav aria-label="Administration" class="border-primary-blue-50 border-b-2">
      <ul
        class="flex flex-row space-x-6 text-sm lg:container lg:mx-auto lg:px-12"
      >
        {#if $isAdmin}
          <li>
            <a
              href="#/admin/system"
              class="ocl-sub-nav-entry {$location.pathname.startsWith(
                '/admin/system',
              )
                ? 'ocl-sub-nav-entry-current'
                : ''}">System Configuration</a
            >
          </li>
          <li>
            <a
              href="#/admin/users"
              class="ocl-sub-nav-entry {$location.pathname.startsWith(
                '/admin/users',
              )
                ? 'ocl-sub-nav-entry-current'
                : ''}">Users</a
            >
          </li>
          <li>
            <a
              href="#/admin/vces"
              class="ocl-sub-nav-entry {$location.pathname.startsWith(
                '/admin/vces',
              )
                ? 'ocl-sub-nav-entry-current'
                : ''}">VCEs</a
            >
          </li>
          <li>
            <a
              href="#/admin/hosts"
              class="ocl-sub-nav-entry {$location.pathname.startsWith(
                '/admin/hosts',
              )
                ? 'ocl-sub-nav-entry-current'
                : ''}">Hosts</a
            >
          </li>
        {/if}
        {#if $isAdmin || $hasClustersAccess}
          <li>
            <a
              href="#/admin/clusters"
              class="ocl-sub-nav-entry {$location.pathname.startsWith(
                '/admin/clusters',
              )
                ? 'ocl-sub-nav-entry-current'
                : ''}">Clusters</a
            >
          </li>
        {/if}
      </ul>
    </nav>
  </div>
{/if}

{#if $isAdmin && $location.pathname.startsWith("/dashboard")}
  <div class="{$ouMenuOpen ? 'block' : 'hidden'} md:block">
    <nav aria-label="Dashboard" class="border-primary-blue-50 border-b-2">
      <ul
        class="flex flex-row space-x-6 text-sm lg:container lg:mx-auto lg:px-12"
      >
        <li>
          <a
            href="#/dashboard/historical"
            class="ocl-sub-nav-entry {$location.pathname.startsWith(
              '/dashboard/historical',
            )
              ? 'ocl-sub-nav-entry-current'
              : ''}">Historical</a
          >
        </li>
        <li>
          <a
            href="#/dashboard/live"
            class="ocl-sub-nav-entry {$location.pathname.startsWith(
              '/dashboard/live',
            )
              ? 'ocl-sub-nav-entry-current'
              : ''}">Live</a
          >
        </li>
      </ul>
    </nav>
  </div>
{/if}

<style>
  .ocl-main-nav-entry {
    @apply border-secondary-mid-blue-350 border-t px-4 py-4 text-sm underline hover:underline md:border-t-2 md:border-transparent md:font-bold md:no-underline;
  }
  .ocl-main-nav-entry-current {
    @apply md:border-secondary-mid-blue md:text-secondary-mid-blue-450 font-bold no-underline md:bg-white;
  }
  .ocl-sub-nav-entry {
    @apply block border-t-2 border-transparent px-4 py-4 text-sm hover:underline;
  }
  .ocl-sub-nav-entry-current {
    @apply border-secondary-mid-blue text-secondary-mid-blue-450 bg-white font-bold;
  }
</style>
